// src/pages/PostEditor.js

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db, storage } from '../firebase';
import { doc, getDoc, collection, getDocs, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import EditorToolbar from '../components/EditorToolbar';
import FontFamily from '@tiptap/extension-font-family';
import { Extension } from '@tiptap/core';
import './PostEditor.css';
import './AdminModals.css';
// O estado inicial completo com todos os campos permanece o mesmo.
const initialState = {
    title: '',
    slug: '',
    author: '',
    mainCategory: '',
    categories: [],
    tags: [],
    richContent: '', // CORRIGIDO
    plainContent: '',
    excerpt: '',
    coverImage: '',
    coverImageDisplayed: true,
    publishedDate: null,
    lastPublishedDate: null,
    timeToRead: 1,
    featured: false,
    pinned: false,
    language: 'pt-BR',
    viewCount: 0,
    likeCount: 0,
    commentCount: 0,
    hashtags: [],
    relatedPosts: [],
    paidPlans: [],
};
const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() { return { types: ['textStyle'] }; },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        fontSize: {
          default: null,
          parseHTML: element => element.style.fontSize.replace(/['"]+/g, ''),
          renderHTML: attributes => {
            if (!attributes.fontSize) { return {}; }
            return { style: `font-size: ${attributes.fontSize}` };
          },
        },
      },
    }];
  },
  addCommands() {
    return {
      setFontSize: (fontSize) => ({ chain }) => {
        return chain().setMark('textStyle', { fontSize }).run();
      },
      unsetFontSize: () => ({ chain }) => {
        return chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run();
      },
    };
  },
});
const PostEditor = () => {
    const { postId } = useParams();
    const navigate = useNavigate();
    const isEditing = Boolean(postId);
    const fileInputRef = useRef(null);
    const categoryFileInputRef = useRef(null);

    const [post, setPost] = useState(initialState);
    const [allCategories, setAllCategories] = useState([]);
    const [allAuthors, setAllAuthors] = useState([]);
    const [allTags, setAllTags] = useState([]);
    const [allPosts, setAllPosts] = useState([]);
    const [isRelatedPostsModalOpen, setRelatedPostsModalOpen] = useState(false);
    const [tempSelectedPosts, setTempSelectedPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [activeTab, setActiveTab] = useState('geral');
    
    const [isAuthorModalOpen, setIsAuthorModalOpen] = useState(false);
    const [newAuthor, setNewAuthor] = useState({ nomeCompleto: '', funcao: '' });
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [newCategory, setNewCategory] = useState({
        label: '',
        slug: '',
        descricao: '',
        coverImage: '',
        language: 'pt-BR'
    });
    const [isCategoryUploading, setIsCategoryUploading] = useState(false);
    // Colocamos a busca de autores em uma função separada para poder reutilizá-la
    
    const fetchAuthors = async () => {
        const authorSnapshot = await getDocs(collection(db, 'autores'));
        const authorList = authorSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllAuthors(authorList);
        return authorList; // Retorna a lista para uso imediato se necessário
    };
    const fetchCategories = async () => {
        const catSnapshot = await getDocs(collection(db, 'blog_categorias'));
        const catList = catSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllCategories(catList);
        return catList;
    };
    
    // Função para gerar slug a partir do título
    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .replace(/ /g, '-')
            .replace(/[^\w-]+/g, '');
    };
    useEffect(() => {
        if (newCategory.label) {
            const newSlug = generateSlug(newCategory.label);
            setNewCategory(prev => ({ ...prev, slug: newSlug }));
        }
    }, [newCategory.label]);


    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                await fetchCategories(); // Usa a nova função
                await fetchAuthors();
                // ... (resto da lógica de fetch) ...
            } catch (error) { /* ... */ }
            finally { setLoading(false); }
        };
        fetchData();
    }, [postId, isEditing, navigate]);
    
    // Efeito para gerar o slug automaticamente quando o título muda
    useEffect(() => {
        if (post.title) {
            const newSlug = generateSlug(post.title);
            setPost(prev => ({ ...prev, slug: newSlug }));
        }
    }, [post.title]);
    useEffect(() => {
        // NOVO: useEffect para gerar o slug da NOVA categoria
        if (newCategory.label) {
            const newSlug = generateSlug(newCategory.label);
            setNewCategory(prev => ({ ...prev, slug: newSlug }));
        }
    }, [newCategory.label]);
    // A definição do editor (useEditor) está correta aqui
    const editor = useEditor({
        extensions: [
            StarterKit.configure({ heading: { levels: [2, 3, 4, 5, 6] } }),
            FontFamily,
            FontSize,
            Underline,
            Link.configure({ openOnClick: false }),
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Highlight,
            TextStyle,
            Color,
        ],
        content: post.richContent,
        onUpdate: ({ editor }) => {
            // A atualização do estado foi movida para cá para garantir consistência
            const htmlContent = editor.getHTML();
            const textContent = editor.getText();
            setPost(prev => ({
                ...prev,
                richContent: htmlContent,
                plainContent: textContent,
            }));
        },
    });
    // useEffect para buscar os dados do Firestore
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // ... (lógica de getDocs para categorias, autores, tags)
                const catSnapshot = await getDocs(collection(db, 'blog_categorias'));
                setAllCategories(catSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                const authorSnapshot = await getDocs(collection(db, 'autores'));
                setAllAuthors(authorSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                const tagSnapshot = await getDocs(collection(db, 'blog_tags'));
                setAllTags(tagSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                await fetchAuthors();
                // NOVO: Fetch de todos os posts para o seletor
                const postsSnapshot = await getDocs(collection(db, 'blog_posts'));
                // Filtra o post atual da lista para não se auto-relacionar
                const postsList = postsSnapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .filter(p => p.id !== postId);
                setAllPosts(postsList);
                if (isEditing) {
                    const postDocRef = doc(db, 'blog_posts', postId);
                    const postSnap = await getDoc(postDocRef);
                    if (postSnap.exists()) {
                        // IMPORTANTE: Garantir que richContent seja uma string
                        const postData = { ...initialState, ...postSnap.data() };
                        if (!Array.isArray(postData.relatedPosts)) {
                            postData.relatedPosts = [];
                        }
                        setPost(postData);
                    } else {
                        navigate('/admin/posts');
                    }
                } else {
                    setPost(initialState);
                }
            } catch (error) {
                console.error("Erro ao carregar dados:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [postId, isEditing, navigate]);
    


    // NOVO useEffect: Este é o responsável por gerenciar o ciclo de vida do Editor.js
    useEffect(() => {
        if (editor && !loading && post.richContent !== editor.getHTML()) {
            editor.commands.setContent(post.richContent);
        }
    }, [post.richContent, loading, editor]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const inputValue = type === 'checkbox' ? checked : value;
        setPost(prev => ({ ...prev, [name]: inputValue }));
    };

    const handleMultiSelectChange = (e) => {
        const { name, options } = e.target;
        const selectedValues = Array.from(options).filter(option => option.selected).map(option => option.value);
        setPost(prev => ({ ...prev, [name]: selectedValues }));
    };

    const handleArrayStringChange = (e) => {
        const { name, value } = e.target;
        const arrayValue = value.split(',').map(item => item.trim()).filter(Boolean);
        setPost(prev => ({ ...prev, [name]: arrayValue }));
    };
    // NOVO: Função para lidar com o upload da imagem de capa
    const handleCoverImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!post.mainCategory) {
            alert("Por favor, selecione a 'Categoria Principal' antes de enviar a imagem.");
            return;
        }

        const category = allCategories.find(cat => cat.id === post.mainCategory);
        if (!category || !category.slug) {
            alert("A categoria principal selecionada é inválida ou não possui um slug.");
            return;
        }

        setIsUploading(true);
        const storageRef = ref(storage, `blog/categorias/${category.slug}/${file.name}`);

        try {
            await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(storageRef);
            setPost(prev => ({ ...prev, coverImage: downloadURL }));
            alert("Imagem enviada com sucesso!");
        } catch (error) {
            console.error("Erro no upload da imagem: ", error);
            alert("Ocorreu um erro ao enviar a imagem de capa.");
        } finally {
            setIsUploading(false);
        }
    };
    const handleOpenAuthorModal = () => setIsAuthorModalOpen(true);

    const handleCloseAuthorModal = () => {
        setIsAuthorModalOpen(false);
        setNewAuthor({ nomeCompleto: '', funcao: '' });
    };

    const handleNewAuthorChange = (e) => {
        const { name, value } = e.target;
        setNewAuthor(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveNewAuthor = async (e) => {
        e.preventDefault();
        if (!newAuthor.nomeCompleto || !newAuthor.funcao) {
            alert("Por favor, preencha o Nome Completo e a Função.");
            return;
        }
        try {
            // Salva o novo autor na coleção 'autores'
            const docRef = await addDoc(collection(db, 'autores'), {
                nomeCompleto: newAuthor.nomeCompleto,
                funcao: newAuthor.funcao,
            });

            // Atualiza a lista de autores no state
            await fetchAuthors();

            // Seleciona automaticamente o autor recém-criado no post atual
            setPost(prev => ({ ...prev, author: docRef.id }));

            alert(`Autor "${newAuthor.nomeCompleto}" adicionado com sucesso!`);
            handleCloseAuthorModal(); // Fecha o modal
        } catch (error) {
            console.error("Erro ao salvar novo autor: ", error);
            alert("Ocorreu um erro ao salvar o novo autor.");
        }
    };
    // --- INÍCIO: NOVAS FUNÇÕES PARA O MODAL DE CATEGORIA ---
    const handleOpenCategoryModal = () => setIsCategoryModalOpen(true);

    const handleCloseCategoryModal = () => {
        setIsCategoryModalOpen(false);
        setNewCategory({ label: '', slug: '', descricao: '', coverImage: '', language: 'pt-BR' });
        setIsCategoryUploading(false);
    };

    const handleNewCategoryChange = (e) => {
        const { name, value } = e.target;
        setNewCategory(prev => ({ ...prev, [name]: value }));
    };
    // NOVA FUNÇÃO para o upload da imagem da categoria
    const handleNewCategoryCoverImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!newCategory.slug) {
            alert("Por favor, preencha o nome da categoria primeiro. O nome é usado para criar a pasta de destino da imagem.");
            return;
        }

        setIsCategoryUploading(true);
        const storageRef = ref(storage, `blog/categorias/${newCategory.slug}/${file.name}`);

        try {
            await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(storageRef);
            setNewCategory(prev => ({ ...prev, coverImage: downloadURL }));
            alert("Imagem da categoria enviada com sucesso!");
        } catch (error) {
            console.error("Erro no upload da imagem da categoria: ", error);
            alert("Ocorreu um erro ao enviar a imagem.");
        } finally {
            setIsCategoryUploading(false);
        }
    };
    // FUNÇÃO DE SALVAR ATUALIZADA
    const handleSaveNewCategory = async (e) => {
        e.preventDefault();
        if (!newCategory.label || !newCategory.slug) {
            alert("Por favor, preencha pelo menos o Nome (Label) da categoria.");
            return;
        }
        try {
            // Salva o objeto completo no Firestore
            const docRef = await addDoc(collection(db, 'blog_categorias'), {
                label: newCategory.label,
                slug: newCategory.slug,
                descricao: newCategory.descricao,
                coverImage: newCategory.coverImage,
                language: newCategory.language,
                postCount: 0,      // Valor inicial padrão
                posts: []          // Valor inicial padrão
            });

            await fetchCategories(); // Atualiza a lista de categorias no editor
            setPost(prev => ({ ...prev, mainCategory: docRef.id })); // Seleciona a nova categoria criada

            alert(`Categoria "${newCategory.label}" adicionada com sucesso!`);
            handleCloseCategoryModal();
        } catch (error) {
            console.error("Erro ao salvar nova categoria: ", error);
            alert("Ocorreu um erro ao salvar a nova categoria.");
        }
    };
    const handleSavePost = async () => {
        if (!post.title || !post.mainCategory || !post.author) {
            alert('Por favor, preencha o Título, Categoria Principal e Autor.');
            return;
        }

        // O editor do TipTap precisa de um momento para registrar a última atualização no estado.
        // Adicionamos uma pequena verificação para garantir que o conteúdo não está vazio.
        if (!editor || editor.isEmpty) {
            alert('O conteúdo do post não pode estar vazio.');
            return;
        }

        setIsSaving(true);
        
        // Com o TipTap, os dados (richContent e plainContent) já estão atualizados no estado 'post'.
        // Não precisamos mais de 'editorInstance.current.save()'.
        const postData = { ...post, lastPublishedDate: serverTimestamp() };
        
        if (!isEditing) {
            postData.publishedDate = serverTimestamp();
            postData.viewCount = 0;
            postData.likeCount = 0;
            postData.commentCount = 0;
        }

        try {
            if (isEditing) {
                await updateDoc(doc(db, 'blog_posts', postId), postData);
            } else {
                await addDoc(collection(db, 'blog_posts'), postData);
            }
            alert('Post salvo com sucesso!');
            navigate('/admin/posts');
        } catch (error) {
            console.error('Erro ao salvar o post: ', error);
            alert('Ocorreu um erro ao salvar o post.');
        } finally {
            setIsSaving(false);
        }
    };
    // --- INÍCIO: NOVAS FUNÇÕES PARA O MODAL DE POSTS RELACIONADOS ---
    const handleOpenRelatedPostsModal = () => {
        // Ao abrir, pré-seleciona os posts que já estão no estado `post`
        setTempSelectedPosts(post.relatedPosts || []);
        setRelatedPostsModalOpen(true);
    };

    const handleCloseRelatedPostsModal = () => {
        setRelatedPostsModalOpen(false);
        setTempSelectedPosts([]); // Limpa a seleção temporária
    };

    const handleRelatedPostSelection = (selectedPostId) => {
        setTempSelectedPosts(prev => 
            prev.includes(selectedPostId)
                ? prev.filter(id => id !== selectedPostId) // Desmarca
                : [...prev, selectedPostId] // Marca
        );
    };

    const handleConfirmRelatedPosts = () => {
        setPost(prev => ({ ...prev, relatedPosts: tempSelectedPosts }));
        handleCloseRelatedPostsModal();
    };

    if (loading) return <div>A carregar editor...</div>;
    const excerptCharCount = post.excerpt.length;
    const excerptLimit = 160;
    const selectedPostObjects = post.relatedPosts
        .map(postId => allPosts.find(p => p.id === postId))
        .filter(Boolean); // Filtra posts que não foram encontrados (caso raro)

    // A estrutura JSX permanece a mesma, pois já está completa.
    return (
        <div className="post-editor-container">
            {/* O modal do autor será renderizado aqui */}
            {isAuthorModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2>Adicionar Novo Autor</h2>
                        <form onSubmit={handleSaveNewAuthor}>
                            <div className="form-group">
                                <label htmlFor="nomeCompleto">Nome Completo</label>
                                <input
                                    type="text"
                                    id="nomeCompleto"
                                    name="nomeCompleto"
                                    value={newAuthor.nomeCompleto}
                                    onChange={handleNewAuthorChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="funcao">Função</label>
                                <input
                                    type="text"
                                    id="funcao"
                                    name="funcao"
                                    value={newAuthor.funcao}
                                    onChange={handleNewAuthorChange}
                                    placeholder="Ex: Escritor"
                                    required
                                />
                            </div>
                            <div className="modal-actions">
                                <button type="button" onClick={handleCloseAuthorModal} className="button-cancel">Cancelar</button>
                                <button type="submit" className="button-save">Salvar Autor</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {isRelatedPostsModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content large">
                        <h2>Selecionar Posts Relacionados</h2>
                        <div className="related-posts-modal-list">
                            {allPosts.map(p => (
                                <div key={p.id} className="related-post-item">
                                    <input 
                                        type="checkbox"
                                        id={`related-${p.id}`}
                                        checked={tempSelectedPosts.includes(p.id)}
                                        onChange={() => handleRelatedPostSelection(p.id)}
                                    />
                                    <label htmlFor={`related-${p.id}`}>{p.title}</label>
                                </div>
                            ))}
                        </div>
                        <div className="modal-actions">
                            <button type="button" onClick={handleCloseRelatedPostsModal} className="button-cancel">Cancelar</button>
                            <button type="button" onClick={handleConfirmRelatedPosts} className="button-save">Confirmar</button>
                        </div>
                    </div>
                </div>
            )}
            <div className="editor-header">
                <h1>{isEditing ? 'Editar Post' : 'Criar Novo Post'}</h1>
                <div className="editor-actions">
                    <button onClick={() => navigate('/admin/posts')} className="button-cancel" disabled={isSaving}>Voltar</button>
                    <button onClick={handleSavePost} className="button-save" disabled={isSaving}>
                        {isSaving ? 'A salvar...' : 'Salvar Post'}
                    </button>
                    
                </div>
                    

            </div>
            {/* --- INÍCIO: MODAL DE CATEGORIA ATUALIZADO --- */}
            {isCategoryModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{maxWidth: '600px'}}>
                        <h2>Criar Nova Categoria</h2>
                        <form onSubmit={handleSaveNewCategory}>
                            <div className="form-group">
                                <label htmlFor="cat-label">Nome da Categoria (Label)</label>
                                <input
                                    type="text" id="cat-label" name="label"
                                    value={newCategory.label} onChange={handleNewCategoryChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="cat-slug">Slug (URL)</label>
                                <input
                                    type="text" id="cat-slug" name="slug"
                                    value={newCategory.slug} onChange={handleNewCategoryChange}
                                    required placeholder="gerado-automaticamente-do-nome"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="cat-descricao">Descrição</label>
                                <textarea 
                                    id="cat-descricao" name="descricao" rows="3"
                                    value={newCategory.descricao} onChange={handleNewCategoryChange}>
                                </textarea>
                            </div>

                            <div className="form-group">
                                <label>Imagem de Capa da Categoria</label>
                                <input 
                                    type="file" 
                                    ref={categoryFileInputRef} 
                                    onChange={handleNewCategoryCoverImageUpload} 
                                    style={{ display: 'none' }} 
                                    accept="image/*"
                                />
                                <button 
                                    type="button" 
                                    className="upload-button"
                                    onClick={() => categoryFileInputRef.current.click()}
                                    disabled={isCategoryUploading}
                                >
                                    {isCategoryUploading ? 'A enviar...' : 'Escolher Imagem'}
                                </button>
                                {newCategory.coverImage && <img src={newCategory.coverImage} alt="Pré-visualização" className="image-preview" />}
                            </div>

                            <div className="modal-actions">
                                <button type="button" onClick={handleCloseCategoryModal} className="button-cancel">Cancelar</button>
                                <button type="submit" className="button-save" disabled={isCategoryUploading}>
                                    {isCategoryUploading ? 'Aguarde o upload...' : 'Salvar Categoria'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <div className="editor-layout">
                <div className="main-editor-area">
                    <input 
                    type="text" 
                    name="title" 
                    className="input-title" 
                    value={post.title}
                    onChange={handleInputChange} // CORREÇÃO: Adicionada a função onChange
                    placeholder="Adicionar título do post"  /*...*/ />
                    <div className="editor-wrapper">
                        {/* Renderiza a nossa barra de ferramentas fixa, passando a instância do editor */}
                        <EditorToolbar editor={editor} />
                        {/* Renderiza a área de texto do editor */}
                        <EditorContent editor={editor} />
                    </div>
                </div>

                <aside className="settings-sidebar">
                    <div className="sidebar-tabs">
                        <button onClick={() => setActiveTab('geral')} className={`tab-button ${activeTab === 'geral' ? 'active' : ''}`}>Geral</button>
                        <button onClick={() => setActiveTab('organizacao')} className={`tab-button ${activeTab === 'organizacao' ? 'active' : ''}`}>Organização</button>
                        <button onClick={() => setActiveTab('seo')} className={`tab-button ${activeTab === 'seo' ? 'active' : ''}`}>SEO</button>
                        <button onClick={() => setActiveTab('metricas')} className={`tab-button ${activeTab === 'metricas' ? 'active' : ''}`}>Métricas</button>
                    </div>

                    {activeTab === 'geral' && (
                        <div className="tab-content">
                            <div className="sidebar-section">
                                <h3>Publicação</h3>
                                <div className="form-group">
                                    <label htmlFor="author">Autor</label>{/* --- INÍCIO DA ALTERAÇÃO NO CAMPO AUTOR --- */}
                                    <div className="form-group-with-button">
                                        <select name="author" id="author" value={post.author} onChange={handleInputChange}>
                                            <option value="">Selecione...</option>
                                            {allAuthors.map(author => (<option key={author.id} value={author.id}>{author.nomeCompleto}</option>))}
                                        </select>
                                        <button type="button" onClick={handleOpenAuthorModal} className="add-new-button">+</button>
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="timeToRead">Tempo de Leitura (min)</label>
                                    <input type="number" name="timeToRead" id="timeToRead" value={post.timeToRead} onChange={handleInputChange} />
                                </div>
                                {isEditing && post.publishedDate && (
                                    <div className="form-group">
                                        <label>Publicado em</label>
                                        <input type="text" readOnly value={new Date(post.publishedDate.seconds * 1000).toLocaleString('pt-BR')} />
                                    </div>
                                )}
                            </div>
                             <div className="sidebar-section">
                                <h3>Visibilidade e Opções</h3>
                                <div className="form-group checkbox-group">
                                    <input type="checkbox" id="featured" name="featured" checked={post.featured} onChange={handleInputChange} />
                                    <label htmlFor="featured">Post em Destaque</label>
                                </div>
                                <div className="form-group checkbox-group">
                                    <input type="checkbox" id="pinned" name="pinned" checked={post.pinned} onChange={handleInputChange} />
                                    <label htmlFor="pinned">Fixar no Topo da Lista</label>
                                </div>
                                <div className="form-group checkbox-group">
                                    <input type="checkbox" id="coverImageDisplayed" name="coverImageDisplayed" checked={post.coverImageDisplayed} onChange={handleInputChange} />
                                    <label htmlFor="coverImageDisplayed">Exibir Imagem de Capa no Post</label>
                                </div>
                            </div>
                             <div className="sidebar-section">
                                <h3>Avançado</h3>
                                {/* --- INÍCIO: CAMPO DE POSTS RELACIONADOS ATUALIZADO --- */}
                                <div className="form-group">
                                    <label>Posts Relacionados</label>
                                    <div className="related-posts-container">
                                        <button type="button" onClick={handleOpenRelatedPostsModal} className="button-link" style={{marginTop: 0}}>
                                            Selecionar Posts
                                        </button>
                                        {selectedPostObjects.length > 0 && (
                                            <ul className="related-posts-list">
                                                {selectedPostObjects.map(p => (
                                                    <li key={p.id}>{p.title}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="relatedPosts">Posts Relacionados</label>
                                    <textarea id="relatedPosts" name="relatedPosts" rows="3" value={Array.isArray(post.relatedPosts) ? post.relatedPosts.join(', ') : ''} onChange={handleArrayStringChange}></textarea>
                                    <small className="helper-text">Insira os IDs dos documentos dos posts.</small>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="paidPlans">Planos Pagos</label>
                                    <textarea id="paidPlans" name="paidPlans" rows="3" value={Array.isArray(post.paidPlans) ? post.paidPlans.join(', ') : ''} onChange={handleArrayStringChange}></textarea>
                                     <small className="helper-text">Futura integração com monetização.</small>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'organizacao' && (
                      <div className="tab-content">
                          <div className="sidebar-section">
                              <h3>Categorização</h3>
                              {/* ... Seletor de Categoria Principal e Secundárias ... */}
                              <div className="form-group">
                                  <label htmlFor="mainCategory">Categoria Principal</label>
                                  <select name="mainCategory" id="mainCategory" value={post.mainCategory} onChange={handleInputChange}>
                                      <option value="">Selecione...</option>
                                      {allCategories.map(cat => (<option key={cat.id} value={cat.id}>{cat.label}</option>))}
                                  </select>
                              </div>
                              
                              <button type="button" onClick={handleOpenCategoryModal} className="button-link">
                                  + Criar nova categoria
                              </button>
                                 <div className="form-group">
                                    <label htmlFor="tags">Tags</label>
                                    <select name="tags" id="tags" value={post.tags} onChange={handleMultiSelectChange} multiple className="multi-select">
                                        {allTags.map(tag => (<option key={tag.id} value={tag.id}>{tag.label}</option>))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="language">Idioma</label>
                                    <input type="text" name="language" id="language" value={post.language} onChange={handleInputChange} />
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {activeTab === 'seo' && (
                         <div className="tab-content">
                             <div className="sidebar-section">
                                <h3>SEO e Mídia</h3>
                                <div className="form-group">
                                    <label htmlFor="slug">URL (Slug)</label>
                                    <input type="text" name="slug" id="slug" value={post.slug} onChange={handleInputChange} placeholder="ex: meu-novo-post-incrivel" />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="excerpt">Resumo (Meta Descrição)</label>
                                    <textarea id="excerpt" name="excerpt" rows="5" value={post.excerpt} onChange={handleInputChange} placeholder="Escreva um resumo atrativo para o Google..."></textarea>
                                    {/* NOVO: Contador de caracteres para o resumo */}
                                    <small className="helper-text" style={{ color: excerptCharCount > excerptLimit ? 'red' : '#6c757d' }}>
                                        {excerptCharCount} / {excerptLimit} caracteres
                                    </small>
                                </div>
                                {/* NOVO: Lógica de Upload da Imagem de Capa */}
                                <div className="form-group">
                                    <label>Imagem de Capa</label>
                                    <input 
                                        type="file" 
                                        ref={fileInputRef} 
                                        onChange={handleCoverImageUpload} 
                                        style={{ display: 'none' }} 
                                        accept="image/*"
                                    />
                                    <button 
                                        type="button" 
                                        className="upload-button" // Estilizar este botão
                                        onClick={() => fileInputRef.current.click()}
                                        disabled={isUploading}
                                    >
                                        {isUploading ? 'A enviar...' : 'Escolher Imagem'}
                                    </button>
                                    {post.coverImage && <img src={post.coverImage} alt="Pré-visualização da capa" className="image-preview" />}
                                    <small className="helper-text">Faça o upload da imagem.</small>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="hashtags">Hashtags (separadas por vírgula)</label>
                                    <input type="text" name="hashtags" id="hashtags" value={Array.isArray(post.hashtags) ? post.hashtags.join(', ') : ''} onChange={handleArrayStringChange} placeholder="#tecnologia, #rpa, #dev"/>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'metricas' && (
                         <div className="tab-content">
                            <div className="sidebar-section">
                                <h3>Métricas (Apenas Leitura)</h3>
                                 <div className="form-group">
                                    <label htmlFor="viewCount">Visualizações</label>
                                    <input type="number" id="viewCount" name="viewCount" value={post.viewCount} readOnly />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="likeCount">Curtidas</label>
                                    <input type="number" id="likeCount" name="likeCount" value={post.likeCount} readOnly />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="commentCount">Comentários</label>
                                    <input type="number" id="commentCount" name="commentCount" value={post.commentCount} readOnly />
                                </div>
                            </div>
                         </div>
                    )}
                </aside>
            </div>
        </div>
    );
};

export default PostEditor;